/**
 * Affordability — compute down-payment + estimated monthly payment
 *
 * Used in two places:
 *  - `PropertyCard` to render the small "est. $X,XXX/mo" line under price
 *  - The mortgage-calculator deep-link to pre-fill the down-payment field
 *
 * Three modes match the backend AffordabilityPrefs:
 *   - 'percentage' — flat % of price
 *   - 'capped'     — prefer %, but cap at max_down_amount
 *   - 'fixed'      — always exactly max_down_amount (clamped to price)
 *
 * When the visitor isn't signed in or hasn't set preferences, we fall
 * back to a 20% down / 30yr / DEFAULT_RATE estimate so the line still
 * shows useful guidance.
 */
import { DEFAULT_MORTGAGE_INPUTS } from '@/types/mortgage';

export type DownPaymentMode = 'percentage' | 'capped' | 'fixed';

export interface AffordabilityPrefs {
    down_payment_mode: DownPaymentMode | null;
    preferred_down_percentage: number | null;
    max_down_amount: number | null;
}

export const DEFAULT_AFFORDABILITY_PREFS: AffordabilityPrefs = {
    down_payment_mode: null,
    preferred_down_percentage: null,
    max_down_amount: null,
};

/**
 * Compute the down-payment dollar amount that applies to a given list price.
 * Falls back to the calculator defaults (20%) when prefs are unset.
 */
export function computeDownPayment(price: number, prefs: AffordabilityPrefs | null | undefined): number {
    if (!Number.isFinite(price) || price <= 0) return 0;

    const mode = prefs?.down_payment_mode ?? null;
    const pct = prefs?.preferred_down_percentage ?? null;
    const max = prefs?.max_down_amount ?? null;

    if (mode === 'fixed' && max !== null && max > 0) {
        return Math.min(max, price);
    }
    if (mode === 'capped' && pct !== null && max !== null && max > 0) {
        return Math.min((price * pct) / 100, max, price);
    }
    if (mode === 'percentage' && pct !== null) {
        return Math.min((price * pct) / 100, price);
    }
    // No prefs / not signed in — use the calculator's default percentage.
    return (price * DEFAULT_MORTGAGE_INPUTS.downPaymentPercentage) / 100;
}

/**
 * Estimate the monthly P&I payment using the standard amortization formula.
 * Pure principal-and-interest — we intentionally omit taxes, insurance, and
 * HOA because the per-card line is a quick benchmark, not a full PITI.
 * The full breakdown lives on the calculator page.
 */
export function estimateMonthlyPayment(
    price: number,
    downPayment: number,
    annualRate: number = DEFAULT_MORTGAGE_INPUTS.interestRate,
    termYears: number = DEFAULT_MORTGAGE_INPUTS.loanTerm,
): number {
    const principal = Math.max(0, price - downPayment);
    if (principal <= 0 || termYears <= 0) return 0;

    const monthlyRate = annualRate / 100 / 12;
    const totalMonths = termYears * 12;
    if (monthlyRate === 0) return principal / totalMonths;

    const factor = Math.pow(1 + monthlyRate, totalMonths);
    return (principal * monthlyRate * factor) / (factor - 1);
}
